<script setup>
import { computed } from 'vue';
import { useMapGetter } from 'dashboard/composables/store.js';
import Icon from 'next/icon/Icon.vue';

const props = defineProps({
  to: { type: [Object, String], default: '' },
  label: { type: String, default: '' },
  icon: { type: [String, Object], default: '' },
  expandable: { type: Boolean, default: false },
  isExpanded: { type: Boolean, default: false },
  isActive: { type: Boolean, default: false },
  hasActiveChild: { type: Boolean, default: false },
  getterKeys: { type: Object, default: () => ({}) },
  count: { type: Number, default: 0 },
  showAlert: { type: Boolean, default: false },
});

const emit = defineEmits(['toggle']);

const showBadge = useMapGetter(props.getterKeys.badge);
const dynamicCount = useMapGetter(props.getterKeys.count);
const displayCountValue = computed(() => props.count || dynamicCount.value);
// Eltafouk: show the real number, no 99+ cap. The agent needs the actual
// backlog size on the comment-inbox channels (volume routinely sits in the
// 500-3000 range and "99+" hides whether things are getting worse or not).
const displayCount = computed(() => displayCountValue.value);
</script>

<template>
  <component
    :is="to ? 'router-link' : 'div'"
    class="flex items-center gap-2 px-1.5 py-1 rounded-lg h-8 min-w-0"
    role="button"
    draggable="false"
    :to="to"
    :title="label"
    :class="{
      'text-n-slate-12 bg-n-alpha-2 font-medium': isActive && !hasActiveChild,
      'text-n-slate-12 font-medium': hasActiveChild,
      'text-n-slate-11 hover:bg-n-alpha-2': !isActive && !hasActiveChild,
    }"
    @click.stop="emit('toggle')"
  >
    <div v-if="icon" class="relative flex items-center gap-2">
      <Icon v-if="icon" :icon="icon" class="size-4" />
      <span
        v-if="showBadge"
        class="size-2 -top-px ltr:-right-px rtl:-left-px bg-n-brand absolute rounded-full border border-n-solid-2"
      />
    </div>
    <div class="flex items-center gap-1.5 flex-grow min-w-0 flex-1">
      <span
        class="truncate"
        :class="{
          'text-body-main': !isActive,
          'font-medium text-sm': isActive || hasActiveChild,
        }"
      >
        {{ label }}
      </span>
      <span
        v-if="displayCountValue"
        dir="ltr"
        class="capitalize leading-5 font-semibold text-center flex-shrink-0 tabular-nums flex items-center justify-center"
        :class="[
          showAlert
            ? 'bg-[#b91c1c] text-white rounded-full outline-none h-[18px] min-w-[18px] px-1 text-[10px] animate-calm-breath'
            : isActive
              ? 'bg-n-alpha-2 text-n-slate-12 outline-n-slate-6 rounded-md outline outline-1 h-5 min-w-5 px-1.5 text-[11px]'
              : 'bg-n-alpha-2 text-n-slate-11 outline-n-strong rounded-md outline outline-1 h-5 min-w-5 px-1.5 text-[11px]',
        ]"
      >
        {{ displayCount }}
      </span>
    </div>
    <span
      v-if="expandable"
      v-show="isExpanded"
      class="i-lucide-chevron-up size-3"
      @click.stop="emit('toggle')"
    />
  </component>
</template>

<style scoped>
@keyframes calm-breath {
  0%,
  100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(185, 28, 28, 0.4);
  }
  50% {
    transform: scale(1.05);
    box-shadow: 0 0 0 4px rgba(185, 28, 28, 0);
  }
}

.animate-calm-breath {
  animation: calm-breath 2.5s ease-in-out infinite;
}
</style>
